import type {SafeHTMLString} from '@github-ui/safe-html'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'

import {LabelToken, type LabelTokenProps} from './LabelToken'

/**
 * The subset of a repository label needed to render a token.
 *
 * Declared structurally rather than imported from a single source so that every
 * label producer in the monorepo satisfies it as-is: Relay fragments, generated
 * server-persisted query types, and hand-written interfaces all expose these
 * fields with these names.
 */
export interface RepositoryLabel {
  readonly name: string
  /**
   * Server-sanitized HTML for the label name, which carries emoji markup. Falls
   * back to `name` for producers that don't select it.
   */
  readonly nameHTML?: string | null
  /** Hex color without the leading `#`, as stored on the label. */
  readonly color: string
  /**
   * Set when the label has been archived. Optional so producers that don't yet
   * select the field (or predate label archiving) stay assignable.
   */
  readonly archivedAt?: string | null
}

/**
 * `label` is omitted from the inherited props because `React.HTMLProps` declares
 * it as the HTML `label` attribute (a string), which would otherwise collide
 * with the label record this component takes.
 */
export interface RepositoryLabelTokenProps extends Omit<
  LabelTokenProps,
  'text' | 'fillColor' | 'isArchived' | 'label'
> {
  label: RepositoryLabel
}

/**
 * Single source of truth for the archived check. `archivedAt` is optional on
 * some producers and strictly nullable on others, so a bare `!== null` reads as
 * archived for any producer whose field is merely absent.
 */
export function isLabelArchived(label: Pick<RepositoryLabel, 'archivedAt'>): boolean {
  return label.archivedAt != null
}

/**
 * The accessible name for a label, including its archived state.
 *
 * Needed because an `aria-label` on an ancestor (a link wrapping the token, for
 * example) replaces the accessible names of everything inside it, including the
 * token's own "Archived" icon. Anything that sets such a label must fold the
 * state back in, and this keeps that wording in one place.
 */
export function labelAccessibleName(label: Pick<RepositoryLabel, 'name' | 'archivedAt'>): string {
  return isLabelArchived(label) ? `Archived ${label.name}` : label.name
}

/**
 * Renders a repository label record as a token.
 *
 * Prefer this over `LabelToken` anywhere a label object is in hand: it owns the
 * color prefixing, the sanitized-name rendering, and the archived treatment, so
 * call sites don't restate them and can't drift from one another. Reach for the
 * `LabelToken` primitive only when there is no label record, such as a badge
 * built from a bare name and color.
 */
export function RepositoryLabelToken({label, ...tokenProps}: RepositoryLabelTokenProps) {
  return (
    <LabelToken
      text={
        label.nameHTML ? (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
          <VerifiedHTMLText html={label.nameHTML as SafeHTMLString} />
        ) : (
          label.name
        )
      }
      fillColor={`#${label.color}`}
      isArchived={isLabelArchived(label)}
      {...tokenProps}
    />
  )
}

RepositoryLabelToken.displayName = 'RepositoryLabelToken'
